/**
 * Choosing which Clerk instance an email address should sign in against.
 *
 * BCE runs two real user pools: the BCE-team instance for staff, and the
 * customer instance for clients. A surface that serves both — myevents, where
 * a customer signs in to see their events and a BCE person signs in to see
 * what that customer sees — has to pick one before it can mount a
 * `ClerkProvider`, because only one can be live on a page.
 *
 * The existing answer (mybcecloud's `auth-mode`) asks the person to pick, via
 * a "BCE staff sign-in" link. This infers it from the address instead, which
 * is right nearly always and wrong harmlessly: the manual override stays, for
 * the BCE employee who is also a client contact.
 *
 * **This is a router, not a gate.** It only decides which instance to *try*.
 * Typing a BCE address grants nothing — the account still has to exist in that
 * instance, and the API still decides what the resulting session may do. The
 * real boundary is server-side, in the auth bridge, which refuses a
 * customer-instance token that resolves to an internal user.
 */

/** The pools a sign-in can be routed to. */
export const SIGN_IN_POOL = {
  /** The BCE-team instance. BCE staff. */
  staff: 'staff',
  /** The customer instance. Clients. */
  client: 'client',
} as const;

/** Which pool an address should authenticate against. */
export type SignInPool = (typeof SIGN_IN_POOL)[keyof typeof SIGN_IN_POOL];

/**
 * Email domains that belong to BCE staff.
 *
 * Mirrors the API's `STAFF_EMAIL_DOMAINS`, but the two serve different
 * purposes and are deliberately not shared: this one routes a login screen,
 * that one authorises provisioning. Drift between them costs a person one
 * confusing sign-in attempt, not an access decision.
 */
export const BCE_STAFF_DOMAINS: readonly string[] = [
  'bestcorporateevents.com',
  'team.bestcorporateevents.com',
  'smarthunts.com',
];

/**
 * The pool an email address should be routed to.
 *
 * Matches the whole domain, never a suffix — `notbestcorporateevents.com` and
 * `bestcorporateevents.com.example` are customers, not staff. An address with
 * no `@`, or nothing after it, routes to the customer pool: that is the
 * larger and less privileged audience, so it is the safer default for input
 * we cannot read.
 *
 * @param email - The address typed on the sign-in screen.
 * @param staffDomains - Override for the staff domain list.
 * @returns Which instance to sign in against.
 */
export function poolForEmail(
  email: string,
  staffDomains: readonly string[] = BCE_STAFF_DOMAINS,
): SignInPool {
  const at = email.lastIndexOf('@');
  if (at === -1) return SIGN_IN_POOL.client;

  const domain = email.slice(at + 1).trim().toLowerCase();
  if (domain.length === 0) return SIGN_IN_POOL.client;

  return staffDomains.includes(domain) ? SIGN_IN_POOL.staff : SIGN_IN_POOL.client;
}
